
import { endOfDay, format, startOfDay } from 'date-fns'
import Litepicker from 'litepicker'
import { defineComponent, PropType } from 'vue'
import { mapGetters, mapState } from 'vuex'
import AppointmentService from '../common/appointmentService'
import AppointmentTypeService from '../common/appointmentTypeService'
import TimeService from '../common/timeService'
import Appointment from '../models/appointment'
import { AppointmentType, AppointmentTypeDetail } from '../models/appointmentType'
import AvailabilitySlot from '../models/availabilitySlot'
import { DynamicInput } from '../models/dynamicInput'
import DynamicField from '../components/DynamicField.vue'
import User from '../models/user'
import UserReassign from '../models/userReassign'
import AppointmentSlotSelection from './AppointmentSlotSelection.vue'
import { cloneDeep } from 'lodash'
import IntakeForm from './IntakeForm.vue'
import FormattedIntakeFormResponse from '../models/formattedIntakeFormResponse'
import addDays from 'date-fns/addDays'
import { propsToAttrMap } from '@vue/shared'

export default defineComponent({
  name: 'AppointmentInfoModal',

  props: {
    appointment: {
      type: Object as PropType<Appointment>,
      required: true
    },
    visible: {
      type: Boolean,
      required: true,
      default: false
    }
  },

  data () {
    return {
      isActive: true,

      // list of available reviewers
      availableReviewers: [] as Array<UserReassign>,
      // List of appointment types
      appointmentTypes: [] as Array<AppointmentType>,
      isBeingReassigned: false,
      isBeingRescheduled: false,
      // reassign user
      reassignUser: null as unknown as User,
      // rescheduling form
      rescheduleForm: {
        step: 1,
        appointmentType: null as any,
        appointmentTypeOfferingId: 0,
        start: null as unknown as Date,
        reviewer: null as unknown as User,
        selectLeastBusyReviewer: false,
        additionalFields: [] as Array<DynamicInput>,
        intakeFormResponses: {},
        intakeFormDifferences: false,
        isActive: true
      },
      intakeForm: null as any,
      newIntakeFormResponse: { isValid: true } as unknown as FormattedIntakeFormResponse,
      isEditingAppointmentForm: false
    }
  },

  mounted () {
    /**
     * get all appointment types
     */
    AppointmentTypeService.getAll().then(res => {
      this.appointmentTypes = res.data.filter(at => at.isTask)
    })
  },

  emits: ['close', 'appointment-cancelled', 'appointment-reassigned', 'appointment-rescheduled', 'appointment-updated'],

  watch: {
    visible: {
      handler (app) {
        this.resetReassign()
        this.resetReschedule()
        this.availableReviewers = []

        if (app) {
          // this.getAvailableReviewers()
        }
      },
      immediate: true
    }
  },

  computed: {
    endOfDayDeadline (): Date {
      return addDays(this.appointment.reviewDeadline, 1)
    },

    /**
     * Sorts available reviewers by workload
     */
    sortedAvailableReviewers (): Array<UserReassign> {
      const reviewers = cloneDeep(this.availableReviewers)
        .sort((a, b) => {
          if (a.workload > b.workload) {
            return 1
          } else if (a.workload < b.workload) {
            return -1
          } else {
            return 0
          }
        })

      if (this.isAdmin) {
        return reviewers
      } else {
        return reviewers.filter(r => !r.hasExternalConflicts && r.availableForSlot)
      }
    },

    /**
     * determines if reschedule form is valid
     */
    isRescheduleFormValid (): boolean {
      const responses = Object.values(this.rescheduleForm.intakeFormResponses)
      return responses.reduce((previous: boolean, current: any) => {
        return previous && current.valid
      }, true) as boolean
    },

    formattedIntakeFormResponse (): FormattedIntakeFormResponse {
      const defaultResponses = {
        pIFirstName: { value: this.appointment.piFirstName },
        pILastName: { value: this.appointment.piLastName },
        pIEmail: { value: this.appointment.piEmail },
        pIPhone: { value: this.appointment.piPhone },
        proposalNumberType: { value: this.appointment.proposalNumberType },
        proposalNumber: { value: this.appointment.proposalNumber }
      }

      return {
        defaultResponses: defaultResponses,
        intakeFormResponses: this.appointment.intakeFormResponse,
        additionalContacts: this.appointment.additionalContacts,
        isValid: true
      }
    },

    /**
     * The display value for the proposal number formatted to include the
     * proposal number type
     */
    proposalNumberDisplay (): string {
      return AppointmentService.formatProposalNumber(this.appointment)
    },

    ...mapState(['userId']),
    ...mapGetters(['isAdmin', 'isReviewer'])
  },

  methods: {
    saveIntakeForm () {
      const params = {
        intakeFormResponse: this.newIntakeFormResponse.intakeFormResponses,
        additionalContacts: this.newIntakeFormResponse.additionalContacts
      }

      const globalResponses = cloneDeep(Object.values(this.newIntakeFormResponse.defaultResponses))
      globalResponses.forEach(gr => {
        params[gr.id] = gr.value
      })

      AppointmentService.updateIntakeForm(this.appointment.id, params).then(res => {
        this.$emit('appointment-updated', res.data)
        this.isEditingAppointmentForm = false
      })
    },

    /**
     * Prepares the appointment intake form for editing
     */
    beginEditingIntakeForm () {
      AppointmentService.getIntakeForm(this.appointment.id).then(res => {
        this.intakeForm = res.data
        this.isEditingAppointmentForm = true
      })
    },

    /**
     * workload in hours conversion
     */
    workloadInHours (workload: number): string {
      const hours = Math.floor(workload / 60)
      const minutes = workload - (hours * 60)
      return `${hours > 0 ? hours : '0'}${hours === 1 ? 'hr' : 'hrs'} ${minutes}min`
    },

    /**
     * reschedule appointment
     */
    reschedule () {
      this.isActive = false
      const params = {} as any
      params.startTime = this.rescheduleForm.start

      params.reviewerId = this.rescheduleForm.reviewer.id
      params.appointmentTypeId = this.rescheduleForm.appointmentType.id
      if (this.rescheduleForm.appointmentTypeOfferingId !== 0) {
        params.appointmentTypeOfferingId = this.rescheduleForm.appointmentTypeOfferingId
      }

      const allFields = cloneDeep(this.appointment.intakeFormResponse)
      const newFields = cloneDeep(Object.values(this.rescheduleForm.intakeFormResponses))
      newFields.forEach((r: any) => {
        if (r.type === 'checkbox group') {
          r.value = r.value.join(', ')
        }
        allFields.push(r)
      })
      params.intakeFormResponse = allFields

      AppointmentService.rescheduleAppointment(this.appointment.id, params).then(res => {
        this.resetReschedule()
        this.isBeingRescheduled = false
        res.data.start = new Date(res.data.start)
        res.data.end = new Date(res.data.end)
        res.data.scheduledOn = new Date(res.data.scheduledOn)
        res.data.reviewDeadline = new Date(res.data.reviewDeadline)
        this.$emit('appointment-rescheduled', res.data)
        this.isActive = true
      })
    },

    /**
     * validates intake form
     */
    updateValid (val, id) {
      this.rescheduleForm.intakeFormResponses[id].valid = val
    },

    /**
     * build new intake form
     */
    constructNewIntakeForm (appointmentType: AppointmentTypeDetail) {
      const diff = appointmentType.currentForm.inputs.filter(input => {
        return this.appointment.intakeFormResponse.find(r => r.label === input.label) == null
      })

      if (diff.length === 0) {
      } else {
        this.rescheduleForm.additionalFields = diff
        const intakeFormResponse = {}
        diff.forEach((input: DynamicInput) => {
          intakeFormResponse[input.id] = {
            id: input.id,
            type: input.type,
            label: input.label,
            value: '',
            required: input.required,
            valid: false
          }

          if (input.type === 'radio') {
            intakeFormResponse[input.id].value = input.options[0]
          }

          if (input.type === 'checkbox group') {
            intakeFormResponse[input.id].value = []
          }
        })
        this.rescheduleForm.intakeFormResponses = intakeFormResponse
        this.rescheduleForm.intakeFormDifferences = true
      }
    },

    /**
     * selects a reviewer for rescheduled appointment
     */
    selectReschedulerReviewer (reviewer: User) {
      this.rescheduleForm.reviewer = reviewer

      if (this.rescheduleForm.appointmentType.isTask) {
        if (this.rescheduleForm.additionalFields.length === 0) {
          this.rescheduleForm.step = 5
        } else {
          this.rescheduleForm.step = 4
        }
      } else {
        this.rescheduleForm.step = 5
      }
    },

    /**
     * handle slot selection
     */
    handleSlotSelection (slot: AvailabilitySlot) {
      this.rescheduleForm.start = slot.start
      this.rescheduleForm.appointmentTypeOfferingId = slot.appointmentTypeOffering.id

      // REFACTOR HERE
      if (!slot.reviewer) {
        AppointmentService.reviewerAvailability(slot.appointmentTypeOffering.id, slot.start).then(res => {
          this.availableReviewers = res.data
          this.rescheduleForm.step = 3
        })
      } else {
        this.rescheduleForm.reviewer = slot.reviewer
        this.rescheduleForm.step = 5
      }
    },

    /**
     * reset reschedule form
     */
    resetReschedule () {
      this.rescheduleForm = {
        step: 1,
        appointmentType: null as any,
        appointmentTypeOfferingId: 0,
        start: null as unknown as Date,
        reviewer: null as unknown as User,
        additionalFields: [],
        selectLeastBusyReviewer: !this.isAdmin,
        intakeFormResponses: {} as object,
        intakeFormDifferences: false,
        isActive: true
      }

      this.isBeingRescheduled = false
    },

    /**
     * select appointment type
     */
    selectAppointmentType (appointentType: AppointmentTypeDetail) {
      this.rescheduleForm.appointmentType = appointentType

      if (this.appointment.appointmentType.id !== appointentType.id) {
        this.constructNewIntakeForm(appointentType)
      }

      if (this.rescheduleForm.appointmentType.isTask) {
        setTimeout(() => {
          const picker = new Litepicker({ element: document.querySelector('.review-date-picker'), inlineMode: true, singleMode: true, onSelect: this.handleReviewDateSelection, disableWeekends: true })
          picker.show()
        }, 0)
      }

      this.rescheduleForm.step = 2
    },

    /**
     * handle review date selection
     */
    handleReviewDateSelection (date: Date) {
      this.rescheduleForm.start = date
      if (this.isAdmin) {
        AppointmentService.reviewerAvailabilityForDate(date).then(res => {
          this.availableReviewers = res.data
          this.rescheduleForm.step = 3
        })
      } else {
        this.rescheduleForm.reviewer = this.appointment.reviewer
        this.rescheduleForm.step = 5
      }
    },

    /**
     * begin reassign process
     */
    beginReassign () {
      this.isBeingReassigned = true
      if (this.appointment.appointmentType.isTask) {
        AppointmentService.reviewerAvailabilityForDate(startOfDay(this.appointment.start)).then(res => {
          this.availableReviewers = res.data
        })
      } else {
        AppointmentService.reviewerAvailability(this.appointment.appointmentTypeOffering.id, this.appointment.start).then(res => {
          this.availableReviewers = res.data
        })
        // this.getAvailableReviewers =
      }
      // AppointmentService.reviewerAvailabilityForDate(this.appointment.start)
    },

    /**
     * cancel appointment
     */
    cancel () {
      if (window.confirm('Are you sure?')) {
        AppointmentService.cancelAppointment(this.appointment.id).then(res => {
          this.$emit('close')
          this.$emit('appointment-cancelled', this.appointment.id)
        })
      }
    },

    /**
     * close form
     */
    close () {
      this.isEditingAppointmentForm = false
      this.$emit('close')
    },

    /**
     * Formates the given number of minutes into a human readable time
     */
    formatMinutes (minutes: number): string {
      return TimeService.minutesToHoursFormatted(minutes)
    },

    /**
     * takes a date returns a string based on given format
     */
    formatTime (date: Date, formatString: string): string {
      return format(date, formatString)
    },

    /**
     * reset reassign process
     */
    resetReassign () {
      this.isBeingReassigned = false
      this.reassignUser = null as unknown as User
    },

    /**
     * reassign appointment
     */
    reassign () {
      this.isActive = false
      AppointmentService.reassign(this.appointment.id, this.reassignUser.id).then(res => {
        this.$emit('appointment-reassigned', res.data)
        this.isActive = true
      })
    }
  },

  components: {
    IntakeForm,
    DynamicField,
    AppointmentSlotSelection
  }
})
